import { useMemo } from 'react';
import _get from 'lodash/get';
import { Widget, WidgetsKindEnum } from 'graphql-common';
import { LocalWidget } from 'routes/Home/hooks/useWidgetEditor';
import useWidgetAccessControl from 'routes/Home/hooks/useWidgetAccessControl';
import prepareWidgetFromFormValues from 'routes/Home/utils/prepareWidgetFromFormValues';
import isWithinOneWeek from 'routes/Home/utils/isWithinOneWeek';
import getFirstActiveAttachmentUrl, {
  AttachmentWithUrls,
} from 'routes/Home/utils/getFirstActiveAttachmentUrl';
import {
  TimeWidget,
  WeatherWidget,
  WeatherWidgetType,
  SitesMapWidget,
  SectorsPerformanceWidget,
  SitesPerformanceWidget,
  ActiveInterventionsWidget,
  QuickCreationWidget,
  ExternalLinkWidget,
  AnnouncementWidget,
} from '../Widgets';
import type { QuickCreationWidgetButtons } from '../Widgets';
import { Location } from '../Widgets/WeatherWidget/types/weather';
import WidgetNoAccessEmptyState from '../WidgetNoAccessEmptyState';

// Grid configuration from widget.grid
export interface WidgetGrid {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface WidgetContentProps {
  widget: Widget | LocalWidget;
  grid?: WidgetGrid;
  /** Whether current home page is the default one - badges only show on default */
  isDefaultHomePage?: boolean;
}

interface LocationJson {
  name: string;
  lat: number;
  lon: number;
}

function parseLocationFromJson(
  locationJson: string | null,
): Location | undefined {
  if (!locationJson) return undefined;

  try {
    const parsed = JSON.parse(locationJson) as LocationJson;
    return {
      name: parsed.name,
      latitude: parsed.lat,
      longitude: parsed.lon,
    };
  } catch {
    return undefined;
  }
}

export default function WidgetContent({
  widget,
  grid,
  isDefaultHomePage = false,
}: WidgetContentProps) {
  const kind = widget.kind as WidgetsKindEnum;

  // Use custom hook for access control
  const {
    isInterventionsLockedByPlan,
    isInterventionsAvailable,
    isSitesAvailable,
    isTasksAvailable,
  } = useWidgetAccessControl();

  // For LocalWidget with formValues, derive widget data from form values
  const localWidget = widget as LocalWidget;
  const derivedWidgetData = useMemo(() => {
    if (localWidget.formValues) {
      // Get original buttons from the original widget data (if editing existing widget)
      // Cast through unknown to handle GraphQL __typename property
      const originalButtons =
        (localWidget.originalWidget?.fields?.buttons as unknown as Record<
          string,
          { enabled: boolean; updatedAt: string }
        >) ||
        (localWidget.fields?.buttons as unknown as Record<
          string,
          { enabled: boolean; updatedAt: string }
        >);
      return prepareWidgetFromFormValues(localWidget.formValues, {
        originalButtons,
      });
    }
    return null;
  }, [
    localWidget.formValues,
    localWidget.originalWidget?.fields?.buttons,
    localWidget.fields?.buttons,
  ]);

  // Use derived data if available, otherwise use widget directly
  const effectiveWidget = derivedWidgetData || widget;

  // Calculate showBadge: only show if updated within 1 week
  const showBadge = useMemo(() => {
    const updatedAt = _get(widget, 'updatedAt') as string | null | undefined;
    return isWithinOneWeek(updatedAt);
  }, [widget]);

  // Memoize weather location to prevent unnecessary WeatherWidget rerenders
  // (parseLocationFromJson creates new object each call, breaking React.memo)
  const weatherLocationJson = _get(effectiveWidget, 'fields.location') as
    string | null;
  const weatherLocation = useMemo(
    () => parseLocationFromJson(weatherLocationJson),
    [weatherLocationJson],
  );

  switch (kind) {
    case WidgetsKindEnum.Clock: {
      const timezone = _get(effectiveWidget, 'fields.timezone') as
        string | null;
      return <TimeWidget timezone={timezone} showBadge={showBadge} />;
    }
    case WidgetsKindEnum.WeatherToday:
      return (
        <WeatherWidget
          type={WeatherWidgetType.Today}
          location={weatherLocation}
          grid={grid}
          showBadge={showBadge}
        />
      );
    case WidgetsKindEnum.WeatherWeek:
      return (
        <WeatherWidget
          type={WeatherWidgetType.Week}
          location={weatherLocation}
          grid={grid}
          showBadge={showBadge}
        />
      );
    case WidgetsKindEnum.SitesMap:
      if (!isSitesAvailable) {
        return <WidgetNoAccessEmptyState />;
      }
      return <SitesMapWidget showBadge={showBadge} />;
    case WidgetsKindEnum.SectorsPerformanceMetricsByDeadlinesMissed:
      if (!isSitesAvailable || !isTasksAvailable) {
        return <WidgetNoAccessEmptyState />;
      }
      return <SectorsPerformanceWidget showBadge={showBadge} />;
    case WidgetsKindEnum.SitesPerformanceMetricsByDeadlinesMissed:
      if (!isSitesAvailable || !isTasksAvailable) {
        return <WidgetNoAccessEmptyState />;
      }
      return <SitesPerformanceWidget showBadge={showBadge} />;
    case WidgetsKindEnum.ActiveInterventions: {
      // Check company-level restriction first (plan-based)
      if (isInterventionsLockedByPlan) {
        return <WidgetNoAccessEmptyState variant="plan" />;
      }
      // Then check user-level access
      if (!isInterventionsAvailable) {
        return <WidgetNoAccessEmptyState />;
      }
      const onlyMy = _get(effectiveWidget, 'fields.onlyMy', false) as boolean;
      return (
        <ActiveInterventionsWidget onlyMy={onlyMy} showBadge={showBadge} />
      );
    }
    case WidgetsKindEnum.QuickCreation: {
      const buttons = _get(effectiveWidget, 'fields.buttons') as
        QuickCreationWidgetButtons | undefined;
      return (
        <QuickCreationWidget
          buttons={buttons}
          isDefaultHomePage={isDefaultHomePage}
        />
      );
    }
    case WidgetsKindEnum.ExternalLink: {
      const externalLinkUrl = _get(
        effectiveWidget,
        'fields.link',
        '',
      ) as string;
      const externalLinkTitle = effectiveWidget.title || externalLinkUrl;
      const externalLinkAttachments = _get(
        effectiveWidget,
        'attachmentsAttached',
        [],
      ) as AttachmentWithUrls[];
      const externalLinkIconUrl = getFirstActiveAttachmentUrl(
        externalLinkAttachments,
      );
      return (
        <ExternalLinkWidget
          title={externalLinkTitle}
          url={externalLinkUrl}
          iconUrl={externalLinkIconUrl}
          showBadge={showBadge}
        />
      );
    }
    case WidgetsKindEnum.Announcement: {
      const announcementTitle = effectiveWidget.title || null;
      const announcementDescription = effectiveWidget.description || null;
      const announcementBackgroundColor =
        (_get(effectiveWidget, 'fields.backgroundColor') as string) || null;
      const announcementAttachments = _get(
        effectiveWidget,
        'attachmentsAttached',
        [],
      ) as AttachmentWithUrls[];
      const announcementImageSrc = getFirstActiveAttachmentUrl(
        announcementAttachments,
      );
      return (
        <AnnouncementWidget
          title={announcementTitle}
          description={announcementDescription}
          backgroundColor={announcementBackgroundColor}
          imageSrc={announcementImageSrc}
          showBadge={showBadge}
        />
      );
    }
    default:
      return <div>Widget: {effectiveWidget.title || kind}</div>;
  }
}
